import { FileAudio, FileText, ImageIcon, Play, Video } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import type { MentionItem } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { safeStorage } from '@/app/utils/client';

export const MentionList = forwardRef<
  { onKeyDown: (event: { key: string }) => boolean },
  {
    items: MentionItem[];
    command: (item: MentionItem) => void;
    query?: string;
    disableTooltip?: boolean;
  }
>(({ items, command, query = '', disableTooltip = false }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hasShownTooltip, setHasShownTooltip] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const showMentionList = items.length > 0;
  const listRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('create');

  // 从 localStorage 检查是否已经显示过 tooltip
  useEffect(() => {
    const hasShown = safeStorage.getItem('MENTION_TOOLTIP_SHOWN');
    if (!hasShown && !disableTooltip) {
      setHasShownTooltip(true);
      setTooltipOpen(true);
      // 将状态保存到 localStorage
      safeStorage.setItem('MENTION_TOOLTIP_SHOWN', 'true');
    }
  }, []);

  const selectItem = (index: number) => {
    const item = items[index];
    if (item) {
      command(item);
      reportEvent('mention_item_click', {
        word_name: item.label,
      });
    }
    setTooltipOpen(false);
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + items.length - 1) % items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ key }) => {
      if (key === 'ArrowUp') {
        upHandler();
        return true;
      }
      if (key === 'ArrowDown') {
        downHandler();
        return true;
      }
      if (key === 'Enter') {
        enterHandler();
        return true;
      }
      return false;
    },
  }));

  // 确保选中项在可视区域内
  const scrollSelectedIntoView = useCallback(() => {
    if (!listRef.current) return;
    const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
    if (!selectedElement) return;
    selectedElement.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  useEffect(() => {
    scrollSelectedIntoView();
  }, [selectedIndex, scrollSelectedIntoView]);

  useEffect(() => {
    // TOFIX: 如果用户输入 query 到没搜到 items，之后又删到有 items，会再报一次曝光
    if (showMentionList) {
      reportEvent('element_show', {
        element_name: items[0].label,
      });
    }
  }, [showMentionList]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  if (!showMentionList) {
    return null;
  }

  // 与 claw media 卡片的 PreviewFallback 视觉对齐：图标外面再嵌一个灰底圆角方块，
  // 使 mention 列表里 markdown / 普通文件等无预览项与 media 区保持一致。
  const renderFallbackIcon = (type?: string) => {
    let Icon = FileText;
    if (type === 'image') Icon = ImageIcon;
    else if (type === 'video') Icon = Video;
    else if (type === 'audio') Icon = FileAudio;
    return (
      <div className="flex size-9 items-center justify-center rounded-8 bg-system-white08 text-system-white40">
        <Icon className="size-4" />
      </div>
    );
  };

  return (
    <div
      ref={listRef}
      className="items-list max-h-[280px] w-[280px] overflow-y-auto rounded-16 bg-[rgba(23,25,28,0.64)] p-2 text-system-white shadow-md backdrop-blur-[30px]"
    >
      {items.map((item, index) => {
        const mediaType = item.mediaType ?? item.type;
        const highlightMatch = (text: string) => {
          if (!query) return <span>{text}</span>;
          const startIndex = text.toLowerCase().indexOf(query.toLowerCase());
          if (startIndex === -1) return <span>{text}</span>;

          const beforeMatch = text.substring(0, startIndex);
          const match = text.substring(startIndex, startIndex + query.length);
          const afterMatch = text.substring(startIndex + query.length);

          return (
            <>
              {beforeMatch && <span>{beforeMatch}</span>}
              <span className="text-system-blue02">{match}</span>
              {afterMatch && <span>{afterMatch}</span>}
            </>
          );
        };

        const buttonContent = (
          <button
            type="button"
            className={cn(
              'flex w-full items-center gap-3 rounded-10 p-2.5 text-left transition-colors',
              'border border-transparent',
              selectedIndex === index
                ? 'border-white/[0.18] bg-white/[0.14]'
                : 'hover:border-white/[0.12] hover:bg-white/[0.08]',
            )}
            key={item.id}
            onClick={() => selectItem(index)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <div className="flex h-12 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-8 bg-black/20 text-system-white48">
              {item.preview && mediaType !== 'audio' && mediaType !== 'file' ? (
                mediaType === 'video' ? (
                  <div className="relative h-full w-full">
                    <video className="h-full w-full object-cover" muted preload="metadata" src={item.preview} />
                    <div className="absolute inset-0 bg-black/20" />
                    <div className="absolute inset-0 flex items-center justify-center text-system-white">
                      <Play className="size-4 fill-current" />
                    </div>
                  </div>
                ) : (
                  <img src={item.preview} alt={item.label} className="h-full w-full object-cover" />
                )
              ) : (
                renderFallbackIcon(mediaType)
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-white">{highlightMatch(item.label)}</div>
              {mediaType && <div className="mt-1 text-[11px] text-system-white40 capitalize">{mediaType}</div>}
            </div>
          </button>
        );

        // 只在第一个按钮且未显示过提示时显示 tooltip（且未禁用 tooltip）
        if (index === 0 && hasShownTooltip && !disableTooltip) {
          return (
            <CustomTooltip
              key={item.id}
              content={t('char2VideoForm.mention.tooltip')}
              trigger="click"
              side="top"
              align="start"
              open={tooltipOpen}
              onOpenChange={setTooltipOpen}
            >
              {buttonContent}
            </CustomTooltip>
          );
        }

        return buttonContent;
      })}
    </div>
  );
});

MentionList.displayName = 'MentionList';
